import { BookOpen, ShieldCheck } from "lucide-react";
import { type ReactNode, useState } from "react";
import acrIntegrationDoc from "../../docs/acr-integration.md?raw";
import adlsPathConventionDoc from "../../docs/adls-path-convention.md?raw";
import apiContractDoc from "../../docs/api-contract.md?raw";
import architectureDoc from "../../docs/architecture.md?raw";
import azureMonitorAlertsDoc from "../../docs/azure-monitor-alerts.md?raw";
import deploymentDoc from "../../docs/deployment.md?raw";
import healthMapUiDoc from "../../docs/health-map-ui.md?raw";
import nodeFailureTroubleshootingDoc from "../../docs/node-failure-troubleshooting.md?raw";
import statusLogicDoc from "../../docs/status-logic.md?raw";
import troubleshootingDoc from "../../docs/troubleshooting.md?raw";
import uatChecklistDoc from "../../docs/uat-checklist.md?raw";

const documents = [
  { path: "docs/architecture.md", content: architectureDoc },
  { path: "docs/status-logic.md", content: statusLogicDoc },
  { path: "docs/node-failure-troubleshooting.md", content: nodeFailureTroubleshootingDoc },
  { path: "docs/api-contract.md", content: apiContractDoc },
  { path: "docs/health-map-ui.md", content: healthMapUiDoc },
  { path: "docs/adls-path-convention.md", content: adlsPathConventionDoc },
  { path: "docs/acr-integration.md", content: acrIntegrationDoc },
  { path: "docs/azure-monitor-alerts.md", content: azureMonitorAlertsDoc },
  { path: "docs/deployment.md", content: deploymentDoc },
  { path: "docs/uat-checklist.md", content: uatChecklistDoc },
  { path: "docs/troubleshooting.md", content: troubleshootingDoc }
];

function renderInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;
    if (token.startsWith("`")) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      nodes.push(
        <a key={key} href={linkMatch?.[2] || "#"} target="_blank" rel="noreferrer">
          {linkMatch?.[1] || token}
        </a>
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function MarkdownDocument({ content }: { content: string }) {
  const lines = content.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = line.match(/^```(\w+)?/);
    if (fence) {
      const language = fence[1];
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      blocks.push(
        <pre className="markdown-code-block" key={`code-${index}`}>
          {language && <span>{language}</span>}
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const headingContent = renderInlineMarkdown(heading[2], `heading-${index}`);
      if (level === 1) {
        blocks.push(<h1 key={`heading-${index}`}>{headingContent}</h1>);
      } else if (level === 2) {
        blocks.push(<h2 key={`heading-${index}`}>{headingContent}</h2>);
      } else if (level === 3) {
        blocks.push(<h3 key={`heading-${index}`}>{headingContent}</h3>);
      } else {
        blocks.push(<h4 key={`heading-${index}`}>{headingContent}</h4>);
      }
      index += 1;
      continue;
    }

    if (line.trim().startsWith("|") && isTableSeparator(lines[index + 1] || "")) {
      const headers = splitTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      blocks.push(
        <div className="markdown-table-scroll" key={`table-${index}`}>
          <table>
            <thead>
              <tr>
                {headers.map((header, headerIndex) => (
                  <th key={`${header}-${headerIndex}`}>{renderInlineMarkdown(header, `th-${index}-${headerIndex}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${index}-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-${cellIndex}`}>{renderInlineMarkdown(cell, `td-${index}-${rowIndex}-${cellIndex}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const listMatch = line.match(/^(\s*)([-*]|\d+\.)\s+(.+)$/);
    if (listMatch) {
      const isOrdered = /\d+\./.test(listMatch[2]);
      const items: string[] = [];
      while (index < lines.length) {
        const itemMatch = lines[index].match(/^(\s*)([-*]|\d+\.)\s+(.+)$/);
        if (!itemMatch || /\d+\./.test(itemMatch[2]) !== isOrdered) {
          break;
        }
        items.push(itemMatch[3]);
        index += 1;
      }
      const ListTag = isOrdered ? "ol" : "ul";
      blocks.push(
        <ListTag key={`list-${index}`}>
          {items.map((item, itemIndex) => (
            <li key={`${item}-${itemIndex}`}>{renderInlineMarkdown(item, `li-${index}-${itemIndex}`)}</li>
          ))}
        </ListTag>
      );
      continue;
    }

    const paragraphLines = [line.trim()];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^#{1,4}\s+/.test(lines[index]) &&
      !/^```/.test(lines[index]) &&
      !/^(\s*)([-*]|\d+\.)\s+/.test(lines[index]) &&
      !(lines[index].trim().startsWith("|") && isTableSeparator(lines[index + 1] || ""))
    ) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }

    blocks.push(
      <p key={`paragraph-${index}`}>
        {renderInlineMarkdown(paragraphLines.join(" "), `paragraph-${index}`)}
      </p>
    );
  }

  return <div className="markdown-document">{blocks}</div>;
}

export function AboutPage() {
  const [selectedDocument, setSelectedDocument] = useState(documents[0]);

  return (
    <section className="page-stack">
      <div className="page-heading">
        <div>
          <p>Reference</p>
          <h2>Documentation / About</h2>
        </div>
      </div>

      <div className="about-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <p>Phase 4 readiness</p>
              <h3>Project Scope</h3>
            </div>
            <BookOpen size={18} aria-hidden="true" />
          </div>
          <p>
            This dashboard monitors scheduled data integration health across mock mode, Azure SQL serving data,
            ADLS raw audit payloads, Synapse polling, ACR task and webhook signals, and Azure Monitor alerts.
          </p>
          <p>
            For incident response, start with <strong>docs/node-failure-troubleshooting.md</strong>. It maps each Health Map node type to source-specific checks.
          </p>
          <div className="doc-link-grid">
            {documents.map((document) => (
              <button
                className={selectedDocument.path === document.path ? "active" : ""}
                key={document.path}
                type="button"
                aria-pressed={selectedDocument.path === document.path}
                onClick={() => setSelectedDocument(document)}
              >
                {document.path}
              </button>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-heading">
            <div>
              <p>Security posture</p>
              <h3>Credential Boundaries</h3>
            </div>
            <ShieldCheck size={18} aria-hidden="true" />
          </div>
          <ul className="plain-list">
            <li>No frontend code connects directly to Azure SQL, ADLS, Synapse, or ACR.</li>
            <li>No real Azure credentials are committed to the repository.</li>
            <li>Environment examples use placeholders only.</li>
            <li>Raw payloads are loaded through Azure Functions, not directly from ADLS in the browser.</li>
          </ul>
        </section>

        <section className="panel doc-viewer-panel" aria-live="polite">
          <div className="panel-heading">
            <div>
              <p>Selected document</p>
              <h3>{selectedDocument.path}</h3>
            </div>
            <BookOpen size={18} aria-hidden="true" />
          </div>
          <MarkdownDocument content={selectedDocument.content} />
        </section>
      </div>
    </section>
  );
}
